import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useUserSearch } from "@/hooks/useUserSearch";
import type { AdminUserSubscription } from "@/services/adminApi";

interface UserComboboxProps {
  /** The selected user's id, as a string (matches scope_id on the wire). */
  value: string | undefined;
  onValueChange: (userId: string | undefined) => void;
  placeholder?: string;
  className?: string;
}

function userLabel(user: AdminUserSubscription): string {
  const email = user.email ?? `user #${user.id}`;
  return `${email} — ${user.subscription_tier}`;
}

export function UserCombobox({
  value,
  onValueChange,
  placeholder = "Search users...",
  className,
}: UserComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useUserSearch(search, 20);
  const users = data?.users ?? [];
  const selected = users.find((u) => String(u.id) === value);

  const handleSelect = (user: AdminUserSubscription) => {
    const id = String(user.id);
    onValueChange(id === value ? undefined : id);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onValueChange(undefined);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("min-w-[240px] justify-between font-normal", className)}
        >
          <span className="truncate">
            {selected
              ? userLabel(selected)
              : value
                ? `user #${value}`
                : placeholder}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            {value && (
              <X
                className="h-4 w-4 opacity-70 hover:opacity-100"
                aria-label="Clear user"
                onClick={handleClear}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by email or name..."
            className="h-9"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>
              {isLoading ? "Searching..." : "No user found."}
            </CommandEmpty>
            <CommandGroup>
              {users.map((user) => (
                <CommandItem
                  key={user.id}
                  value={String(user.id)}
                  onSelect={() => handleSelect(user)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === String(user.id) ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {userLabel(user)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
